import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { Borrower } from '../../../models/borrower-model';
import { TypeOfIncome } from '../../../models/enums/type-of-income.enum';
import { Income } from '../../../models/income-model';
import { EnumsService } from '../../../services/enums.service';
import { BorrowerPicker } from '../../borrower-picker/borrower-picker.component';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';
import * as _ from 'lodash';
import { isUrlaOtherIncomeSource } from './urla-other-income';

@Component({
    selector: 'simple-income-dialog',
    templateUrl: 'simple-income-dialog.component.html',
    standalone: false
})
export class SimpleIncomeDialogComponent extends ConfigurableFieldHostComponent implements OnInit {

  @ViewChild('incomeInfoForm') incomeInfoForm: NgForm | undefined;
  @ViewChild(BorrowerPicker) borrowerPicker: BorrowerPicker | undefined;

  @Input() borrowers: Borrower[] = [];

  @Input()
  public set income(income: Income) {
    this._income = income;
    this._editorIncomeType = this.income.typeOfIncome;
    this._inEditMode = true;
  }

  public get income(): Income {
    return this._income;
  }

  public header: string | undefined;
  public label: string | undefined;

  public get inEditMode(): boolean {
    return this._inEditMode;
  }

  otherIncomeTypes: EnumerationItem[] = [];

  set editorIncomeType(typeOfIncome: TypeOfIncome) {
    this._editorIncomeType = typeOfIncome;
    this._income.typeOfIncome = this._editorIncomeType;
  }

  get editorIncomeType(): TypeOfIncome {
    return this._editorIncomeType;
  }

  get showEcoaOtherIncomeNotice(): boolean {
    return isUrlaOtherIncomeSource(this.editorIncomeType);
  }

  private _income: Income;
  private _inEditMode: boolean = false;
  private _editorIncomeType: TypeOfIncome = TypeOfIncome.OtherTypesOfIncome;

  constructor(public activeModal: NgbActiveModal,
    private readonly _enumsService: EnumsService) {
    super();
    this._income = new Income();
    this._income.typeOfIncome = this._editorIncomeType;
    this._enumsService.getMortgageEnumerations().subscribe(enums => {
      const filtered = enums['IncomeType'].filter(t => this.filterIncomesTypesThatAreNotInOtherCategory(t.value));
      // Translate each enum's display name through `enums.incomeType.{value}`
      // before sorting — otherwise Spanish users see English labels and the
      // order is also based on English words.
      const translated = this._enumsService.translateEnumArray(filtered, 'incomeType');
      this.otherIncomeTypes = _.orderBy(translated, x => x.name);
    });
  }

  onSaveClicked = () => {
    this.incomeInfoForm?.form.markAllAsTouched();
    let borrowerValid = true;
    if (this.borrowerPicker) {
      borrowerValid = this.borrowerPicker.validate();
    }
    if (this.incomeInfoForm?.form.valid && borrowerValid) {
      this.activeModal.close(this._income);
    }
  }

  ngOnInit() {
    // Only default the borrowerId for truly NEW (add-mode) incomes. In
    // edit mode a client-side-only income still has incomeId = 0, so the
    // legacy `!incomeId` check would re-default it and clobber the user's
    // previously-chosen borrower on every re-open.
    if (!this._inEditMode && !this._income.borrowerId && this.borrowers.length > 0) {
      this._income.borrowerId = this.borrowers[0].borrowerId;
    }
  }

  private filterIncomesTypesThatAreNotInOtherCategory = (incomeType: string): boolean => {
    return !(incomeType == TypeOfIncome.Base ||
      incomeType == TypeOfIncome.Bonus ||
      incomeType == TypeOfIncome.Commissions ||
      incomeType == TypeOfIncome.Pension ||
      incomeType == TypeOfIncome.SocialSecurity ||
      incomeType == TypeOfIncome.NetRentalIncome ||
      incomeType == TypeOfIncome.MilitaryBasePay ||
      incomeType == TypeOfIncome.SelfEmploymentIncome ||
      incomeType == TypeOfIncome.ContractBasis ||
      incomeType == TypeOfIncome.Commissions ||
      incomeType == TypeOfIncome.Overtime);
  }
}
